/**
 * The document's own language — the `<html lang>` attribute (card `t_8ca38a80`).
 *
 * `index.html` (and `shell.html`) ship `lang="en"` as the pre-boot default and
 * nothing in the app ever moved it, so a session running in `sl` — the business
 * side's first language — told every consumer of the document's language that
 * the page is English: a screen reader picks its voice and profile from this
 * attribute, the browser decides whether to offer a translation, and
 * hyphenation, spell-check and `:lang()` selectors all read it too. None of them
 * can see the app's own locale store.
 *
 * The value is not a second language table. It is `localeTag()`
 * (`utils/formatDate.ts`) — the one spelling this tree already carries for the
 * app's locale: the catalogues' five pinned regions (`sl-SI`, `de-DE`, …), an
 * English UI following the runtime's own region (`en-GB`, `en-US`), and
 * `en-GB` when the runtime reports no English region at all.
 *
 * Read off the **locale store**, not off vue-i18n's `locale`: the store is the
 * app's own source of truth for the reader's language (`otto-locale`), it is
 * what `tt()` and every date format already read, and a watcher on it catches
 * every writer — the boot path (`App.vue` → `initializeLocale()`), the account
 * preferences' language picker (`Account.vue` → `setLocale()`), and any writer
 * added later. A watcher on the i18n instance would miss the boot path: the
 * store is initialized from `localStorage` in `onMounted`, so the two disagree
 * for a moment at startup.
 *
 * `index.html`'s static `lang="en"` stays exactly as it is: it is what the
 * document says before any app code has run, and it is true until the stored
 * locale is applied.
 */

import { watch } from 'vue';
import { localeTag } from './formatDate';
import { useLocaleStore } from '../stores/locale';

/**
 * Write the app's locale onto the document. This is the **one** place that
 * touches `documentElement.lang`; a runtime with no document (this repo's tests
 * run under `node --test`, with no DOM) is left alone rather than throwing.
 */
export function applyDocumentLang(): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = localeTag();
}

/**
 * Keep the attribute equal to the locale store — now, and on every change.
 * Returns the watcher's stop handle, so a component's setup scope (or a test)
 * can dispose it; the app's own entry never stops it.
 */
export function installDocumentLang(): () => void {
  const locale = useLocaleStore();
  return watch(() => locale.currentLocale, applyDocumentLang, { immediate: true });
}
